import {
    ChangeDetectionStrategy,
    Component,
    computed,
    EventEmitter,
    inject,
    Input,
    Output,
    Signal,
    signal
} from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';
import { OrderItemComponent } from '../order-item/order-item.component';
import { NgForTrackByPropertyDirective, Order, OrderItem } from '@us/web/shared/data-access';
import { EmptyComponent } from '@us/web/shared/ui';
import { Store } from '@ngrx/store';
import { selectPersistedUserId } from '@us/web/auth/data-access';
import { FormatCurrencyPipe } from '@us/web/shop/data-access';

export type QuantityChangedOutputType = { item: OrderItem; quantity: number };

@Component({
    selector: 'us-order-items',
    standalone: true,
    imports: [
        NgIf,
        NgForOf,
        TranslocoModule,
        OrderItemComponent,
        EmptyComponent,
        NgForTrackByPropertyDirective,
        FormatCurrencyPipe
    ],
    templateUrl: './order-items.component.html',
    styleUrls: ['./order-items.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderItemsComponent {
    private readonly store = inject(Store);
    readonly currentUserId = this.store.selectSignal(selectPersistedUserId);

    private readonly _order = signal(null as Order | null);
    readonly isCartShared = computed(() => this._order()?.is_shared || false);

    readonly items: Signal<
        { is_owner: boolean; items: OrderItem[]; id: string; name: string; subtotal: number; total_items: number }[]
    > = computed(() => {
        const order = this._order();
        const userId = this.currentUserId();
        if (!order) {
            return [];
        }

        const members = order.is_shared
            ? order.group_members || []
            : [
                  {
                      id: this.currentUserId(),
                      name: ''
                  }
              ];

        const result = members.map(member => {
            const items: OrderItem[] = [];
            let subTotal = 0;
            let totalItems = 0;

            order.items.forEach(item => {
                if (!item.user?.id || item.user?.id === member?.id) {
                    items.push(item);
                    subTotal += item.quantity * item.price; // TODO
                    totalItems += item.quantity;
                }
            });

            return {
                ...member,
                is_owner: member.is_owner || false,
                items,
                subtotal: subTotal,
                total_items: totalItems
            };
        });

        const currentUserItems = result.find(item => item.id === userId);
        if (currentUserItems) {
            result.splice(result.indexOf(currentUserItems), 1);
            result.unshift(currentUserItems);
        }

        return result;
    });

    @Input() set order(order: Order) {
        this._order.set(order);
    }

    @Input() readonly = false;
    @Output() quantityChanged: EventEmitter<QuantityChangedOutputType> = new EventEmitter();

    onChangeItemQuantity(item: OrderItem, quantity: number): void {
        this.quantityChanged.emit({ item, quantity });
    }
}
